import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { InputLoadingIndicator } from "../InputLoadingIndicator";
import { MicButton } from "../MicButton";
import { MenuDropdown } from "./MenuDropdown";
import { EmojiSelector } from "./EmojiSelector";
import { HelpDialog } from "./HelpDialog";
import { useMicrophonePermission } from "@/hooks/use-microphone-permission";
import { useSpeechRecognition } from "@/hooks/use-speech-recognition";
import { useMobileDetection, useStandaloneDetection } from "../hooks";
export interface TodoInputBarProps {
  isLoading: boolean;
  apiKey: string;
  onAction: (text: string, emoji: string) => void;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
}
export function TodoInputBar(t0) {
  const $ = _c(60);
  const {
    isLoading,
    apiKey,
    onAction,
    inputRef
  } = t0;
  const [newTodo, setNewTodo] = useState("");
  const [selectedEmoji, setSelectedEmoji] = useState("\uD83D\uDE0A");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showFaqDialog, setShowFaqDialog] = useState(false);
  const [hasUsedApp, setHasUsedApp] = useState(false);
  const isMobile = useMobileDetection();
  const isStandalone = useStandaloneDetection();
  const micPermission = useMicrophonePermission();
  const {
    isRecording,
    isProcessingSpeech,
    startRecording,
    stopRecording
  } = useSpeechRecognition(apiKey);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const hasUsed = localStorage.getItem("vif-has-used");
      setHasUsedApp(!!hasUsed);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== hasUsedApp) {
    t3 = () => {
      if (!hasUsedApp) {
        localStorage.setItem("vif-has-used", "true");
        setHasUsedApp(true);
      }
    };
    $[2] = hasUsedApp;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const markAppAsUsed = t3;
  let t4;
  if ($[4] !== isLoading || $[5] !== markAppAsUsed || $[6] !== newTodo || $[7] !== onAction || $[8] !== selectedEmoji) {
    t4 = e => {
      if (e.key === "Enter" && !e.shiftKey && !isLoading) {
        e.preventDefault();
        const trimmedText = newTodo.trim();
        if (trimmedText) {
          onAction(trimmedText, selectedEmoji);
          setNewTodo("");
          markAppAsUsed();
        }
      }
    };
    $[4] = isLoading;
    $[5] = markAppAsUsed;
    $[6] = newTodo;
    $[7] = onAction;
    $[8] = selectedEmoji;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleInputKeyDown = t4;
  let t5;
  if ($[10] !== isRecording || $[11] !== markAppAsUsed || $[12] !== onAction || $[13] !== selectedEmoji || $[14] !== stopRecording) {
    t5 = async () => {
      if (isRecording) {
        const text = await stopRecording();
        if (text) {
          onAction(text, selectedEmoji);
          markAppAsUsed();
        }
      }
    };
    $[10] = isRecording;
    $[11] = markAppAsUsed;
    $[12] = onAction;
    $[13] = selectedEmoji;
    $[14] = stopRecording;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleSpeechResult = t5;
  let t6;
  if ($[16] !== markAppAsUsed || $[17] !== newTodo || $[18] !== onAction || $[19] !== selectedEmoji) {
    t6 = () => {
      const trimmedText_0 = newTodo.trim();
      if (trimmedText_0) {
        onAction(trimmedText_0, selectedEmoji);
        setNewTodo("");
        markAppAsUsed();
      }
    };
    $[16] = markAppAsUsed;
    $[17] = newTodo;
    $[18] = onAction;
    $[19] = selectedEmoji;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleSendAction = t6;
  const t7 = isStandalone && "pb-8";
  const t8 = isInputFocused && "pb-4";
  let t9;
  if ($[21] !== t7 || $[22] !== t8) {
    t9 = cn("fixed bottom-0 left-0 right-0 p-4 bg-background border-t transition-all duration-200 ease-in-out", t7, t8);
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <MenuDropdown />;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== isLoading || $[26] !== selectedEmoji) {
    t11 = <EmojiSelector selectedEmoji={selectedEmoji} isLoading={isLoading} onEmojiSelect={setSelectedEmoji} />;
    $[25] = isLoading;
    $[26] = selectedEmoji;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== handleInputKeyDown || $[29] !== inputRef || $[30] !== isLoading || $[31] !== isProcessingSpeech || $[32] !== newTodo) {
    t12 = <div className="flex-1 flex items-center min-h-[44px]">{isLoading || isProcessingSpeech ? <InputLoadingIndicator showText={!isProcessingSpeech} /> : <Textarea ref={inputRef} placeholder="add task or action" value={newTodo} onChange={e_0 => setNewTodo(e_0.target.value)} onKeyDown={handleInputKeyDown} onFocus={() => setIsInputFocused(true)} onBlur={() => setIsInputFocused(false)} autoResize={true} rows={1} className={cn("flex-1 border-0 !bg-transparent focus:!outline-none focus:!ring-0 focus-visible:!ring-0 focus-visible:!ring-offset-0 min-h-[44px] max-h-[200px] rounded-none shadow-none px-3 py-2 resize-none leading-normal")} disabled={isLoading || isProcessingSpeech} />}</div>;
    $[28] = handleInputKeyDown;
    $[29] = inputRef;
    $[30] = isLoading;
    $[31] = isProcessingSpeech;
    $[32] = newTodo;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== newTodo) {
    t13 = newTodo.trim();
    $[34] = newTodo;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = !!t13;
  let t15;
  if ($[36] !== handleSendAction || $[37] !== handleSpeechResult || $[38] !== isProcessingSpeech || $[39] !== isRecording || $[40] !== micPermission || $[41] !== startRecording || $[42] !== t14) {
    t15 = <MicButton isRecording={isRecording} isProcessingSpeech={isProcessingSpeech} micPermission={micPermission} startRecording={startRecording} stopRecording={handleSpeechResult} hasText={t14} onSend={handleSendAction} />;
    $[36] = handleSendAction;
    $[37] = handleSpeechResult;
    $[38] = isProcessingSpeech;
    $[39] = isRecording;
    $[40] = micPermission;
    $[41] = startRecording;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t11 || $[45] !== t12 || $[46] !== t15) {
    t16 = <div className="flex-1 flex items-center bg-muted/80 rounded-lg overflow-hidden min-h-[44px]">{t11}{t12}{t15}</div>;
    $[44] = t11;
    $[45] = t12;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== hasUsedApp || $[49] !== isLoading || $[50] !== isMobile || $[51] !== isProcessingSpeech || $[52] !== showFaqDialog) {
    t17 = !hasUsedApp && <HelpDialog isMobile={isMobile} showFaqDialog={showFaqDialog} isLoading={isLoading} isProcessingSpeech={isProcessingSpeech} onFaqDialogChange={setShowFaqDialog} />;
    $[48] = hasUsedApp;
    $[49] = isLoading;
    $[50] = isMobile;
    $[51] = isProcessingSpeech;
    $[52] = showFaqDialog;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== t16 || $[55] !== t17) {
    t18 = <div className="max-w-md mx-auto flex items-center gap-2">{t10}{t16}{t17}</div>;
    $[54] = t16;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== t18 || $[58] !== t9) {
    t19 = <div className={t9}>{t18}</div>;
    $[57] = t18;
    $[58] = t9;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  return t19;
}